import {
  AuthenticationStatus as Auth,
  StatusDeviceConnectionStatus as Connection,
  StatusStreamState as Stream
} from '@tryiris-ai/common.protojs/agent/v1/agent';

/**
 * Overall outcome of verifying a saved configuration. Saving and verifying are separate: every
 * value here means the configuration was saved; it only says what the camera has confirmed since.
 *
 * - `verified`: every configured aspect (video, control) confirmed the new configuration.
 * - `video-verified`: video plays on the new configuration, but control never confirmed. The
 *   camera works; the UI shows a warning, never a rejection.
 * - `auth-required`: the camera explicitly rejected the credentials for at least one aspect.
 * - `connection-failed`: the camera reported a failure that lasted to the deadline and no
 *   working video acknowledges the configuration.
 * - `unverified`: nothing was confirmed or rejected before the deadline (or verification was
 *   cancelled). Absence of evidence is not a rejection.
 */
export type ConnectionVerification =
  | 'verified'
  | 'video-verified'
  | 'auth-required'
  | 'connection-failed'
  | 'unverified';

/**
 * What one aspect (video or control) has confirmed. `pending` covers absent, loading, and
 * still-validating statuses — per rule `absent-data-gates-permissively` (ai.knowledge/global)
 * missing data never reads as a failure.
 */
export type AspectOutcome = 'verified' | 'rejected' | 'failed' | 'pending';

/** Outcome per configured aspect; an aspect the device does not configure is omitted. */
export type AspectOutcomes = { readonly video?: AspectOutcome; readonly control?: AspectOutcome };

/** How the UI presents a verification result: green, yellow, or red. */
export type VerificationSeverity = 'success' | 'warning' | 'error';

type Aspect = {
  readonly authenticationStatus?: Auth | undefined;
  readonly connectionStatus?: { readonly status?: Connection | undefined } | undefined;
};
type VideoAspect = Aspect & {
  readonly status?: { readonly state?: Stream | undefined } | undefined;
};
export type VerificationStatus =
  | { readonly video?: VideoAspect | undefined; readonly movement?: Aspect | undefined }
  | undefined;
export type VerificationAspects = {
  video: boolean;
  control: boolean;
  /** VISCA control has no credential verdict, so only its live connection is evidence. */
  controlConnectionOnly?: boolean;
};

/**
 * VISCA has no credential exchange and reports FAILED until its first reply: an UNKNOWN verdict
 * reads as unsupported, and only CONNECTED confirms the aspect. A published verdict is kept as is.
 */
const connectionEvidence = (aspect: Aspect | undefined): Aspect | undefined =>
  aspect && !aspect.authenticationStatus
    ? {
        authenticationStatus: Auth.AUTHENTICATION_STATUS_UNSUPPORTED,
        connectionStatus:
          aspect.connectionStatus?.status === Connection.CONNECTION_STATUS_CONNECTED
            ? aspect.connectionStatus
            : undefined
      }
    : aspect;

/** Unknown authentication stays pending once connected, unless the protocol never reports one. */
const aspectOutcome = (aspect: Aspect | undefined, streamState?: Stream): AspectOutcome => {
  const auth = aspect?.authenticationStatus;
  if (auth === Auth.AUTHENTICATION_STATUS_INVALID) return 'rejected';
  if (auth === Auth.AUTHENTICATION_STATUS_ERROR) return 'failed';
  const accepted =
    auth === Auth.AUTHENTICATION_STATUS_VALIDATED ||
    auth === Auth.AUTHENTICATION_STATUS_UNSUPPORTED;
  // Older bridges can publish stream state without the optional connection message.
  // Per wire-contract-changes-must-not-break-old-firmware (global).
  const connection = aspect?.connectionStatus
    ? aspect.connectionStatus.status
    : streamState === Stream.STREAM_STATE_PLAYING
      ? Connection.CONNECTION_STATUS_CONNECTED
      : streamState === Stream.STREAM_STATE_FAIL
        ? Connection.CONNECTION_STATUS_FAILED
        : undefined;
  if (accepted && connection === Connection.CONNECTION_STATUS_FAILED) return 'failed';
  if (accepted && connection === Connection.CONNECTION_STATUS_CONNECTED) return 'verified';
  return 'pending';
};

/**
 * Evaluate video and control independently, so a working stream is acknowledged even while the
 * control plane is still unresolved, and a rejection is attributed to the aspect that issued it.
 */
export const aspectOutcomes = (
  status: VerificationStatus,
  aspects: VerificationAspects
): AspectOutcomes => ({
  ...(aspects.video ? { video: aspectOutcome(status?.video, status?.video?.status?.state) } : {}),
  ...(aspects.control
    ? {
        control: aspectOutcome(
          aspects.controlConnectionOnly ? connectionEvidence(status?.movement) : status?.movement
        )
      }
    : {})
});

/** Intermediate verdict while verifying; `undefined` while any configured aspect is pending. */
export const connectionVerdict = (
  status: VerificationStatus,
  aspects: VerificationAspects
): ConnectionVerification | undefined => {
  const outcomes = Object.values(aspectOutcomes(status, aspects));
  if (outcomes.includes('rejected')) return 'auth-required';
  if (outcomes.includes('failed')) return 'connection-failed';
  if (outcomes.length && outcomes.every((outcome) => outcome === 'verified')) return 'verified';
  return undefined;
};

/**
 * The verdict when the window closes. Working video acknowledges the configuration even if
 * control never confirmed or is still failing (a control connection failure is not a credential
 * rejection). A failure is reported only when video does not vouch for the camera.
 */
const deadlineVerdict = (outcomes: AspectOutcomes | undefined): ConnectionVerification => {
  if (!outcomes) return 'unverified';
  if (outcomes.video === 'verified') return 'video-verified';
  if (Object.values(outcomes).includes('failed')) return 'connection-failed';
  return 'unverified';
};

/**
 * Only a confirmed connection or a rejection is final. The bridge publishes FAILED while a camera's
 * control plane is still starting, so a failure is held and reported only if it lasts to the deadline.
 */
const isFinal = (
  verdict: ConnectionVerification | undefined
): verdict is 'verified' | 'auth-required' => verdict === 'verified' || verdict === 'auth-required';

/** Handle on an in-flight verification. */
export type ConnectionVerificationWatcher = {
  /** Resolves once with the verdict; every terminal path (verdict, deadline, stop) settles it. */
  wait: () => Promise<ConnectionVerification>;
  /** Cancel: releases the listener and timer and settles any waiter as `unverified`. */
  stop: () => void;
  /**
   * Mark the new configuration as landed (call once its add/replace command resolved). Any status
   * update observed after this point belongs to the new configuration.
   */
  commit: () => void;
  /** Per-aspect outcome from the latest fresh status; `pending` for aspects with no fresh evidence. */
  aspects: () => AspectOutcomes;
};

/**
 * Watch the device status for the verdict on a configuration that is about to be committed.
 *
 * Freshness is generation-aware: device status carries no revision of its own, so the watcher
 * counts the updates it observes and `commit()` records the generation at which the new
 * configuration landed. A status is fresh once an update newer than that generation arrives, or
 * once the old status was seen to disappear. Before either, the status may still be the previous
 * configuration's (whose credentials may already be validated) and is ignored. The old rule —
 * accept only after observing the status disappear — hung until the deadline whenever the bridge's
 * remove and re-add reached the client without an observable gap.
 */
export const watchConnectionVerification = (
  read: () => VerificationStatus,
  subscribe: (refresh: () => void) => () => void,
  aspects: VerificationAspects,
  timeoutMs = 30_000
): ConnectionVerificationWatcher => {
  // Count of status updates observed since the watch began.
  let generation = 0;
  // Generation at which `commit()` ran; updates strictly after it are fresh.
  let committedAt: number | undefined;
  // The previous configuration's status was seen to disappear: anything after it is the new one.
  let cleared = read() === undefined;
  let outcomes: AspectOutcomes | undefined;
  let verdict: ConnectionVerification | undefined;
  let finish: ((result: ConnectionVerification) => void) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const isFresh = () => cleared || (committedAt !== undefined && generation > committedAt);
  const evaluate = () => {
    const status = read();
    if (!status) cleared = true;
    if (!isFresh()) {
      verdict = undefined;
      return;
    }
    outcomes = aspectOutcomes(status, aspects);
    verdict = connectionVerdict(status, aspects);
    if (isFinal(verdict)) finish?.(verdict);
  };
  const onUpdate = () => {
    generation += 1;
    evaluate();
  };

  const unsubscribe = subscribe(onUpdate);
  evaluate();

  // Per rule `settle-every-terminal-branch` (ai.knowledge/global): verdict, deadline, and
  // cancellation all funnel through `stop`, which releases the listener and timer and settles
  // any waiter. `finish` is a promise resolver, so only the first settlement takes effect.
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    unsubscribe();
    // Cancellation is never evidence that the camera accepted credentials.
    finish?.('unverified');
  };

  return {
    wait: () =>
      new Promise<ConnectionVerification>((resolve) => {
        finish = resolve;
        if (stopped) {
          resolve('unverified');
          return;
        }
        evaluate();
        if (isFinal(verdict)) {
          stop();
          return;
        }
        timer = setTimeout(() => {
          resolve(deadlineVerdict(outcomes));
          stop();
        }, timeoutMs);
      }).finally(stop),
    stop,
    commit: () => {
      committedAt = generation;
    },
    aspects: () =>
      outcomes ?? {
        ...(aspects.video ? { video: 'pending' as const } : {}),
        ...(aspects.control ? { control: 'pending' as const } : {})
      }
  };
};

/** Green only when everything confirmed; red only for a rejection or a failure video cannot vouch for. */
export const verificationSeverity = (result: ConnectionVerification): VerificationSeverity => {
  switch (result) {
    case 'verified':
      return 'success';
    case 'video-verified':
    case 'unverified':
      return 'warning';
    case 'auth-required':
    case 'connection-failed':
      return 'error';
  }
};

/** Short heading for a non-verified result, so a working camera is never titled as a failure. */
export const verificationTitle = (result: ConnectionVerification): string => {
  switch (result) {
    case 'verified':
      return 'Connection verified';
    case 'video-verified':
      return 'Camera control not verified';
    case 'auth-required':
      return 'Credentials rejected';
    case 'connection-failed':
      return 'Connection failed';
    case 'unverified':
      return 'Connection not verified';
  }
};

/**
 * Saved configuration and verified connectivity are separate outcomes. With `aspects`, a
 * rejection names the aspect that issued it.
 */
export const verificationMessage = (
  result: ConnectionVerification,
  aspects?: AspectOutcomes
): string => {
  switch (result) {
    case 'verified':
      return 'Connection verified.';
    case 'video-verified':
      return 'Video is connected, but camera control has not confirmed the connection yet. Pan, tilt, and zoom may not respond. Check the control settings, then try again.';
    case 'auth-required':
      if (aspects?.video === 'rejected' && aspects.control !== 'rejected')
        return 'The camera rejected the video credentials. Enter the camera’s username and password, then try again.';
      if (aspects?.control === 'rejected' && aspects.video !== 'rejected')
        return 'The camera rejected the camera control credentials. Enter the camera’s username and password, then try again.';
      return 'The camera rejected the credentials. Enter the camera’s username and password, then try again.';
    case 'connection-failed':
      return 'Could not verify the camera connection. Check the connection settings and try again.';
    case 'unverified':
      return 'The camera has not confirmed the connection yet. Check that it is reachable, then try again.';
  }
};
